import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Sparkles, Save, Download, Copy, Trash2, RefreshCw, AlertTriangle, CheckCircle2,
  XCircle, FileText, Settings2, Archive, Plus, ChevronDown, ChevronRight, Search,
  Loader2, ExternalLink, ImageIcon, MapPin, Target,
} from 'lucide-react';
import { apiFetch } from '../../utils/apiFetch';
import { tohumBul } from './icerikTohumlari';
import LokasyonStudyosu from './LokasyonStudyosu';
import {
  markdownUret, htmlUret, jsonLdUret, duzMetinUret, indir, panoyaKopyala, zipUret, dosyaAdi,
} from './icerikDisaAktar';

/**
 * İçerik Stüdyosu — marka sınırlarıyla SEO yazısı üret, düzenle, arşivle, dışa aktar.
 *
 * Üç sekme, üç iş:
 *   Üret     — brief gir (ya da hazır brief havuzundan seç) → Gemini yazsın → düzenle
 *   Sınırlar — markanın kendi kuralları: ton, uzunluk, yasak ifadeler, iç link eşiği
 *   Arşiv    — kaydedilen taslaklar, durumları ve toplu ZIP dışa aktarım
 *
 * 🔑 SINIRLAR İKİ KEZ İŞ GÖRÜYOR. Bir kez prompt'ta (modele ne isteneceğini söyler),
 * bir kez de üretimden sonra denetimde (modelin uyup uymadığını ÖLÇER). Sağdaki
 * denetim paneli modelin iddiasını değil metnin kendisini ölçüyor; elle düzenleme
 * yaptıkça yeniden çalışıyor.
 *
 * Marka bağımsız: sınırlar backend'de marka şemasında saklanıyor, brief havuzu
 * markaya göre geliyor (icerikTohumlari.js). Europcar tohumu blog envanteri ve
 * kelime araştırmasından (/r/europcar-blog) türetildi.
 *
 * Sidebar'a bilerek BAĞLI DEĞİL — doğrudan /icerik/studyo adresinden açılıyor.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();
const API = `${API_BASE}/api/content-studio`;

const DURUMLAR = [
  { k: 'taslak', ad: 'Taslak', renk: 'bg-slate-100 text-slate-600' },
  { k: 'incelemede', ad: 'İncelemede', renk: 'bg-amber-100 text-amber-700' },
  { k: 'onayli', ad: 'Onaylı', renk: 'bg-emerald-100 text-emerald-700' },
  { k: 'yayinda', ad: 'Yayında', renk: 'bg-blue-100 text-blue-700' },
];
const durumBul = (k) => DURUMLAR.find((d) => d.k === k) || DURUMLAR[0];

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const satirlaraBol = (s) => String(s || '').split('\n').map((t) => t.trim()).filter(Boolean);
const paragraflaraBol = (s) => String(s || '').split(/\n{2,}/).map((t) => t.trim()).filter(Boolean);

/* ───────────────────────── küçük arayüz parçaları ───────────────────────── */

// Kontrollü metin alanı; dışarıdaki değeri YALNIZCA odak dışındayken alır.
// Aksi halde her tuş vuruşunda üstteki state yeniden yazılıp imleç başa dönüyor.
function Alan({ deger, onIsle, satir = 3, ipucu = '', className = '' }) {
  const [t, setT] = useState(deger ?? '');
  const [odakta, setOdakta] = useState(false);
  useEffect(() => { if (!odakta) setT(deger ?? ''); }, [deger, odakta]);
  return (
    <textarea
      rows={satir}
      value={t}
      placeholder={ipucu}
      onFocus={() => setOdakta(true)}
      onChange={(e) => setT(e.target.value)}
      onBlur={() => { setOdakta(false); onIsle(t); }}
      className={`w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-[13px] leading-relaxed text-slate-800 outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 ${className}`}
    />
  );
}

function TekSatir({ deger, onIsle, ipucu = '', limit = null, className = '' }) {
  const [t, setT] = useState(deger ?? '');
  const [odakta, setOdakta] = useState(false);
  useEffect(() => { if (!odakta) setT(deger ?? ''); }, [deger, odakta]);
  const uzun = limit && t.length > limit;
  return (
    <div className="relative">
      <input
        value={t}
        placeholder={ipucu}
        onFocus={() => setOdakta(true)}
        onChange={(e) => setT(e.target.value)}
        onBlur={() => { setOdakta(false); onIsle(t); }}
        className={`w-full rounded-lg border px-3 py-2 text-[13px] text-slate-800 outline-none focus:ring-1 focus:ring-emerald-100 ${uzun ? 'border-amber-300' : 'border-slate-200 focus:border-emerald-400'} ${limit ? 'pr-14' : ''} ${className}`}
      />
      {limit ? (
        <span className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] tabular-nums ${uzun ? 'text-amber-600' : 'text-slate-400'}`}>
          {t.length}/{limit}
        </span>
      ) : null}
    </div>
  );
}

function Sayi({ deger, onIsle, adim = 1, min = 0 }) {
  return (
    <input
      type="number"
      value={deger}
      min={min}
      step={adim}
      onChange={(e) => onIsle(e.target.value === '' ? '' : Number(e.target.value))}
      className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] tabular-nums text-slate-800 outline-none focus:border-emerald-400"
    />
  );
}

const Etiket = ({ children, not }) => (
  <div className="mb-1 flex items-baseline gap-2">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{children}</span>
    {not ? <span className="text-[11px] text-slate-400">{not}</span> : null}
  </div>
);

function Kutu({ baslik, sag, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      {(baslik || sag) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-700">{baslik}</h3>
          {sag}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/* ─────────────────────────────── ana bileşen ─────────────────────────────── */

const BOS_BRIEF = {
  baslik: '', odakKelime: '', yardimciKelimeler: [], sorular: [], aramaNiyeti: '', notlar: '',
  olgular: '',
};

/* SEO gerekçesi: müşterinin yazı başına sorduğu altı soru. Sıra ekranda da,
   dışa aktarımda da budur. Anahtarlar sunucudaki SEO_GEREKCE_ALANLARI ile birebir. */
const GEREKCE_ALANLARI = [
  { anahtar: 'sorguNiyet', etiket: 'Hedeflenen ana sorgu ve arama niyeti',
    ipucu: 'Hangi sorgu için yazıldı, kullanıcı o sorguyu yazarken ne arıyor?' },
  { anahtar: 'veriGerekce', etiket: 'Konu seçimini destekleyen veri ve öncelik gerekçesi',
    ipucu: 'Gösterim, tık, sıra. Neden bu konu, neden şimdi?' },
  { anahtar: 'mevcutSayfa', etiket: 'Aynı ihtiyacı karşılayan mevcut sayfa',
    ipucu: 'Blogda ya da sitede karşılığı var mı?' },
  { anahtar: 'yeniMiGelistirme', etiket: 'Yeni sayfa mı, mevcudun geliştirilmesi mi?',
    ipucu: 'Tercihin gerekçesi. Kannibalizasyon riski varsa nasıl ayrıştırıldı?' },
  { anahtar: 'icLinkHedef', etiket: 'Planlanan iç bağlantılar ve yönlendirilen sayfa',
    ipucu: 'Hangi yazılara bağlanıyor, okuru hangi hizmet ya da rezervasyon sayfasına taşıyor?' },
  { anahtar: 'kpi', etiket: 'Yayın sonrası takip edilecek başarı göstergesi',
    ipucu: 'Neye bakıp başarılı diyeceğiz? Ölçülebilir olsun.' },
];

const BOS_GEREKCE = GEREKCE_ALANLARI.reduce((o, a) => ({ ...o, [a.anahtar]: '' }), { kaynak: 'elle' });

export default function ContentStudio() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [boot, setBoot] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [sekme, setSekme] = useState('uret');

  const [sinirlar, setSinirlar] = useState(null);
  const [sinirKirli, setSinirKirli] = useState(false);

  const [brief, setBrief] = useState(BOS_BRIEF);
  const [icerik, setIcerik] = useState(null);
  const [denetim, setDenetim] = useState(null);
  const [model, setModel] = useState(null);
  const [uretiliyor, setUretiliyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [taslakId, setTaslakId] = useState(null);
  const [taslaklar, setTaslaklar] = useState([]);
  const [secili, setSecili] = useState(() => new Set());
  const [benzer, setBenzer] = useState(null);
  const [briefAcik, setBriefAcik] = useState(true);
  // Kapak görseli: dosyanın kendisi icerik.gorsel içinde duruyor (kaydet/yükle/
  // dışa aktarım onu kendiliğinden taşısın diye). Buradakiler sadece ekran hâli.
  const [gorselUretiliyor, setGorselUretiliyor] = useState(false);
  const [gorselIstem, setGorselIstem] = useState('');
  const [gorselEnBoy, setGorselEnBoy] = useState('16:9');
  const [istemAcik, setIstemAcik] = useState(false);
  const [aciKume, setAciKume] = useState(null);
  // Hazır havuzun durumu: hangi brief zaten yazıldı. Cevap sunucudan geliyor
  // (arşiv + blog envanteri orada), havuzun kendisi marka tohumunda duruyor.
  const [briefDurum, setBriefDurum] = useState(null);
  const [kullanilanGoster, setKullanilanGoster] = useState(false);
  // SEO gerekçesi: "bu yazı neden yazıldı" sorusunun altı başlıklık cevabı.
  // Yazıyla birlikte kaydediliyor; müşteriye giden dosyanın 3. bölümü buradan çıkıyor.
  const [seoGerekce, setSeoGerekce] = useState(BOS_GEREKCE);
  const [gerekceDolduruluyor, setGerekceDolduruluyor] = useState(false);
  const [gerekceOlcum, setGerekceOlcum] = useState(null);

  const tohum = useMemo(() => tohumBul(brandId), [brandId]);

  /* ───────────────────────────── yükleme ───────────────────────────── */

  const yukle = useCallback(async () => {
    if (!brandId) { setHata('Marka seçili değil. Üst çubuktan bir marka seçin.'); setYukleniyor(false); return; }
    setYukleniyor(true);
    try {
      const res = await apiFetch(`${API}/bootstrap?_t=${Date.now()}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Stüdyo yüklenemedi');
      setBoot(json);
      setTaslaklar(json.taslaklar || []);
      // Kayıtlı profil yoksa markanın tohumu, o da yoksa backend varsayılanı.
      setSinirlar(json.profilKayitli ? json.profil : { ...json.profil, ...(tohum?.profil || {}) });
      setSinirKirli(!json.profilKayitli && !!tohum);
      setHata(null);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [brandId, headers, tohum]);

  useEffect(() => { yukle(); }, [yukle]);

  // Arşiv her değiştiğinde havuz yeniden işaretleniyor: bir brief'i kaydettiğin
  // anda listeden düşsün diye. `taslaklar` bilerek bağımlılıkta.
  useEffect(() => {
    const havuz = tohum?.briefler;
    if (!brandId || !havuz?.length) { setBriefDurum(null); return; }
    let iptal = false;
    (async () => {
      try {
        const res = await apiFetch(`${API}/brief-durum`, {
          method: 'POST', headers,
          body: JSON.stringify({
            dil: sinirlar?.dil || 'tr',
            briefler: havuz.map((b) => ({ no: b.no, baslik: b.baslik, odakKelime: b.odakKelime })),
          }),
        });
        const json = await res.json();
        if (iptal || !res.ok || json.error) return;
        const harita = {};
        (json.durumlar || []).forEach((d) => { harita[d.no] = d; });
        setBriefDurum(harita);
      } catch { /* havuz işaretsiz kalır, sayfa çalışmaya devam eder */ }
    })();
    return () => { iptal = true; };
  }, [brandId, headers, tohum, taslaklar, sinirlar?.dil]);

  /* ──────────────────────── denetim (gecikmeli) ────────────────────── */

  const denetimZaman = useRef(null);
  useEffect(() => {
    if (!icerik || !sinirlar) return;
    clearTimeout(denetimZaman.current);
    denetimZaman.current = setTimeout(async () => {
      try {
        const res = await apiFetch(`${API}/audit`, {
          method: 'POST', headers,
          body: JSON.stringify({ icerik, sinirlar, brief, seoGerekce }),
        });
        const json = await res.json();
        if (res.ok) setDenetim(json.denetim);
      } catch { /* denetim sessizce başarısız olabilir, üretimi engellemesin */ }
    }, 900);
    return () => clearTimeout(denetimZaman.current);
  }, [icerik, sinirlar, brief, seoGerekce, headers]);

  /* ──────────────────────────── eylemler ───────────────────────────── */

  const sinirlariKaydet = async () => {
    try {
      const res = await apiFetch(`${API}/profile`, {
        method: 'PUT', headers, body: JSON.stringify({ profil: sinirlar }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Kaydedilemedi');
      setSinirlar(json.profil);
      setSinirKirli(false);
      toast.success('Marka sınırları kaydedildi');
    } catch (e) { toast.error(e.message); }
  };

  const yamyamlikBak = useCallback(async () => {
    if (!brief.baslik && !brief.odakKelime) return null;
    try {
      const res = await apiFetch(`${API}/cannibal-check`, {
        method: 'POST', headers,
        body: JSON.stringify({ baslik: brief.baslik, odakKelime: brief.odakKelime, dil: sinirlar?.dil }),
      });
      const json = await res.json();
      if (res.ok) { setBenzer(json); return json; }
    } catch { /* yoksay */ }
    return null;
  }, [brief.baslik, brief.odakKelime, sinirlar?.dil, headers]);

  /* Gerekçeyi elde olan veriden doldurur: Search Console sorgu ailesi + blog
     envanteri örtüşmesi. Çıktı taslaktır, üzerine yazılabilir. Doldurulmuş
     alanlar korunur — düğme yazdıklarınızı ezmez. */
  const gerekceDoldur = async () => {
    if (!brief.odakKelime && !icerik?.odakKelime && !icerik?.baslik && !brief.baslik) {
      toast.error('Önce odak kelime ya da başlık gerekli');
      return;
    }
    setGerekceDolduruluyor(true);
    try {
      const res = await apiFetch(`${API}/seo-gerekce`, {
        method: 'POST', headers,
        body: JSON.stringify({ brief, icerik: icerik || {}, dil: sinirlar?.dil }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Doldurulamadı');
      const gelen = json.gerekce || {};
      setSeoGerekce((eski) => {
        const yeni = { ...eski };
        let degisen = 0;
        GEREKCE_ALANLARI.forEach(({ anahtar }) => {
          if (!String(eski[anahtar] || '').trim() && String(gelen[anahtar] || '').trim()) {
            yeni[anahtar] = gelen[anahtar];
            degisen += 1;
          }
        });
        yeni.kaynak = degisen ? 'otomatik' : eski.kaynak;
        return yeni;
      });
      setGerekceOlcum(gelen.olcum || null);
      toast.success('Veriden dolduruldu, gözden geçirin');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGerekceDolduruluyor(false);
    }
  };

  const uret = async () => {
    if (!brief.baslik && !brief.odakKelime) {
      toast.error('Başlık fikri ya da odak anahtar kelime gerekli');
      return;
    }
    setUretiliyor(true);
    setBenzer(null);
    try {
      // Yamyamlık kontrolü üretimi ENGELLEMEZ, sadece sonucun yanında durur:
      // bazen bilerek daha iyi bir yazıyla eskisini değiştiriyoruz.
      yamyamlikBak();
      const res = await apiFetch(`${API}/generate`, {
        method: 'POST', headers, body: JSON.stringify({ brief, sinirlar }),
      });
      // Uzun üretimde backend nabız baytı akıtıyor ve hatayı 200 gövdesinde
      // {error} olarak döndürüyor (bayt aktıktan sonra durum kodu değişemiyor),
      // o yüzden res.ok tek başına yetmiyor.
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Üretim başarısız');
      setIcerik(json.icerik);
      // İstem kutusu yazıyla birlikte gelsin: taslakta görsel varsa onun istemi,
      // yeni üretimde boş (bir sonraki görsel yeni yazıdan çıkarılacak).
      setGorselIstem(json.icerik?.gorsel?.istem || '');
      setDenetim(json.denetim);
      setModel(json.model);
      // Yeni yazı yeni gerekçe ister; öncekinin gerekçesi buraya taşınmamalı.
      setSeoGerekce(BOS_GEREKCE);
      setGerekceOlcum(null);
      setTaslakId(null);
      setBriefAcik(false);
      (json.uyarilar || []).forEach((u) => toast(u, { icon: '⚠️' }));
      toast.success(`Yazı hazır · ${json.model} · ${(json.sureMs / 1000).toFixed(0)} sn`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUretiliyor(false);
    }
  };

  /**
   * Kapak görseli. İstem kutusu BOŞSA modele yazıyı okutup istemi o yazdırıyor;
   * doluysa kullanıcının istemi aynen çiziliyor — düzeltilmiş istem ikinci
   * denemede model tarafından ezilmesin diye.
   */
  const gorselUret = async () => {
    if (!icerik?.baslik) { toast.error('Önce yazıyı üret'); return; }
    setGorselUretiliyor(true);
    try {
      const res = await apiFetch(`${API}/image`, {
        method: 'POST', headers,
        body: JSON.stringify({
          icerik, sinirlar,
          enBoy: gorselEnBoy,
          istem: gorselIstem.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Görsel üretilemedi');
      gncIcerik({ gorsel: json.gorsel });
      setGorselIstem(json.gorsel.istem || '');
      toast.success(`Görsel hazır · ${(json.sureMs / 1000).toFixed(0)} sn`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGorselUretiliyor(false);
    }
  };

  const kaydet = async (durum) => {
    if (!icerik?.baslik) { toast.error('Kaydedilecek içerik yok'); return; }
    setKaydediliyor(true);
    try {
      const res = await apiFetch(`${API}/drafts`, {
        method: 'POST', headers,
        body: JSON.stringify({ id: taslakId, icerik, brief, sinirlar, model, seoGerekce, durum: durum || 'taslak' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Kaydedilemedi');
      setTaslakId(json.id);
      setDenetim(json.denetim);
      toast.success(taslakId ? 'Taslak güncellendi' : 'Taslak arşive eklendi');
      const b = await apiFetch(`${API}/bootstrap?_t=${Date.now()}`, { headers });
      const bj = await b.json();
      if (b.ok) setTaslaklar(bj.taslaklar || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setKaydediliyor(false);
    }
  };

  const taslakAc = async (id) => {
    try {
      const res = await apiFetch(`${API}/drafts/${id}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Taslak açılamadı');
      setIcerik(json.icerik);
      // İstem kutusu yazıyla birlikte gelsin: taslakta görsel varsa onun istemi,
      // yeni üretimde boş (bir sonraki görsel yeni yazıdan çıkarılacak).
      setGorselIstem(json.icerik?.gorsel?.istem || '');
      setBrief(json.brief || BOS_BRIEF);
      // Taslağın yazıldığı andaki sınırlar kayıtta duruyor ama YÜKLENMİYOR: eski
      // bir taslağı açmak markanın güncel sınırlarını sessizce geri almamalı.
      // Denetim de bilerek güncel sınırlara göre yeniden hesaplanıyor.
      setDenetim(json.denetim);
      setModel(json.model);
      setSeoGerekce({ ...BOS_GEREKCE, ...(json.seoGerekce || {}) });
      setGerekceOlcum(json.seoGerekce?.olcum || null);
      setTaslakId(json.id);
      setSekme('uret');
      setBriefAcik(false);
    } catch (e) { toast.error(e.message); }
  };

  const taslakSil = async (id) => {
    if (!window.confirm('Bu taslak kalıcı olarak silinsin mi?')) return;
    try {
      const res = await apiFetch(`${API}/drafts/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Silinemedi');
      setTaslaklar((t) => t.filter((x) => x.id !== id));
      if (taslakId === id) setTaslakId(null);
      toast.success('Taslak silindi');
    } catch (e) { toast.error(e.message); }
  };

  const durumDegistir = async (id, durum) => {
    try {
      const res = await apiFetch(`${API}/drafts/${id}/status`, {
        method: 'PATCH', headers, body: JSON.stringify({ durum }),
      });
      if (!res.ok) throw new Error('Durum güncellenemedi');
      setTaslaklar((t) => t.map((x) => (x.id === id ? { ...x, durum } : x)));
    } catch (e) { toast.error(e.message); }
  };

  /* ──────────────────────────── dışa aktarım ───────────────────────── */

  const ustveri = useMemo(() => ({
    marka: boot?.marka?.ad,
    dil: sinirlar?.dil,
    durum: taslakId ? (taslaklar.find((t) => t.id === taslakId)?.durum || 'taslak') : 'taslak',
    odakKelime: brief.odakKelime,
    kelimeSayisi: denetim?.ozet?.kelime,
    model,
    seoGerekce,
  }), [boot, sinirlar, taslakId, taslaklar, brief.odakKelime, denetim, model, seoGerekce]);

  const disaAktar = async (bicim) => {
    if (!icerik) return;
    if (bicim === 'md') indir(markdownUret(icerik, ustveri), dosyaAdi(icerik, 'md'), 'text/markdown;charset=utf-8');
    if (bicim === 'html') indir(htmlUret(icerik, { ustveri }), dosyaAdi(icerik, 'html'), 'text/html;charset=utf-8');
    if (bicim === 'json') indir(JSON.stringify({ ustveri, brief, sinirlar, icerik, denetim }, null, 2), dosyaAdi(icerik, 'json'), 'application/json');
    if (bicim === 'kopyala-duz') {
      await panoyaKopyala(duzMetinUret(icerik)); toast.success('Düz metin panoya kopyalandı');
    }
    if (bicim === 'kopyala-md') {
      await panoyaKopyala(markdownUret(icerik, ustveri)); toast.success('Markdown panoya kopyalandı');
    }
    if (bicim === 'kopyala-html') {
      await panoyaKopyala(htmlUret(icerik, { jsonLd: false })); toast.success('HTML panoya kopyalandı');
    }
    if (bicim === 'kopyala-jsonld') {
      await panoyaKopyala(jsonLdUret(icerik, ustveri)); toast.success('JSON-LD panoya kopyalandı');
    }
  };

  const topluAktar = async () => {
    const idler = [...secili];
    if (!idler.length) { toast.error('Önce taslak seçin'); return; }
    try {
      const res = await apiFetch(`${API}/export?ids=${idler.join(',')}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Dışa aktarılamadı');
      const blob = await zipUret(json.kayitlar, json.marka);
      const ad = `${(json.marka || 'marka').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-icerik-${new Date().toISOString().slice(0, 10)}.zip`;
      indir(blob, ad, 'application/zip');
      toast.success(`${json.adet} yazı indirildi`);
    } catch (e) { toast.error(e.message); }
  };

  /* ───────────────────────── içerik düzenleyiciler ─────────────────── */

  const gncIcerik = (yama) => setIcerik((o) => ({ ...(o || {}), ...yama }));
  const gncBolum = (i, yama) =>
    setIcerik((o) => {
      const b = [...(o.bolumler || [])];
      b[i] = { ...b[i], ...yama };
      return { ...o, bolumler: b };
    });
  const bolumSil = (i) =>
    setIcerik((o) => ({ ...o, bolumler: (o.bolumler || []).filter((_, k) => k !== i) }));
  const bolumEkle = () =>
    setIcerik((o) => ({ ...o, bolumler: [...(o.bolumler || []), { h2: 'Yeni başlık', paragraflar: [''] }] }));
  const gncSss = (i, yama) =>
    setIcerik((o) => {
      const s = [...(o.sss || [])];
      s[i] = { ...s[i], ...yama };
      return { ...o, sss: s };
    });

  const briefDoldur = (b) => {
    setBrief({
      baslik: b.baslik || '',
      odakKelime: b.odakKelime || '',
      yardimciKelimeler: b.yardimciKelimeler || [],
      sorular: b.sorular || [],
      aramaNiyeti: b.aramaNiyeti || '',
      notlar: [b.bosluk, b.notlar].filter(Boolean).join(' '),
      // Havuzdan geldiğinin izi. Taslakla birlikte kaydediliyor; bir dahaki
      // işaretlemede başlık benzerliğine gerek kalmadan birebir eşleşiyor.
      tohumNo: b.no,
    });
    setBenzer(null);
    setBriefAcik(true);
    toast.success(`Brief yüklendi: ${b.baslik?.slice(0, 40)}…`);
  };

  /* ────────────────────────────── görünüm ──────────────────────────── */

  if (yukleniyor) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (hata) {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-16">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
          <p className="mt-3 text-[15px] font-semibold text-slate-800">İçerik Stüdyosu açılamadı</p>
          <p className="mt-1 text-[13px] text-slate-600">{hata}</p>
          <button onClick={yukle} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-[13px] font-medium text-white">
            Yeniden dene
          </button>
        </div>
      </div>
    );
  }

  const puan = denetim?.ozet?.puan ?? null;
  const puanRenk = puan === null ? 'text-slate-400'
    : puan >= 90 ? 'text-emerald-600' : puan >= 70 ? 'text-amber-600' : 'text-rose-600';

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6">
      {/* ── Başlık ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">
            {boot?.marka?.ad} · İçerik Stüdyosu
          </h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Sınırları belli, SEO uyumlu blog içeriği
          </p>
          <p className="mt-1.5 max-w-[720px] text-[13px] leading-relaxed text-slate-500">
            Markanın kendi kurallarıyla yazdır, üretimden sonra ölç, arşivle ve dışa aktar.
            Sınırlar yalnızca modele söylenmiyor; üretilen metnin üzerinde <strong className="font-semibold text-slate-700">ölçülüyor</strong>.
            {boot?.envanter?.adet
              ? ` Blog envanterindeki ${nf(boot.envanter.adet)} yazı iç link havuzu ve tekrar kontrolü olarak kullanılıyor.`
              : ' Bu markada blog envanteri yok — iç link önerisi ve tekrar kontrolü çalışmaz.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!boot?.geminiHazir && (
            <span className="rounded-lg bg-rose-50 px-2.5 py-1.5 text-[12px] font-medium text-rose-700">
              Gemini anahtarı yok
            </span>
          )}
          <button
            onClick={yukle}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Yenile
          </button>
        </div>
      </div>

      {/* ── Sekmeler ── */}
      <div className="mt-5 flex items-center gap-1 border-b border-slate-200">
        {[
          { k: 'uret', ad: 'Üret', ikon: Sparkles },
          { k: 'sinirlar', ad: 'Sınırlar', ikon: Settings2, rozet: sinirKirli ? '•' : null },
          { k: 'lokasyon', ad: 'Lokasyon', ikon: MapPin },
          { k: 'arsiv', ad: `Arşiv (${taslaklar.length})`, ikon: Archive },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setSekme(s.k)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-[13px] font-semibold transition ${
              sekme === s.k
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <s.ikon className="h-3.5 w-3.5" /> {s.ad}
            {s.rozet ? <span className="text-amber-500">{s.rozet}</span> : null}
          </button>
        ))}
      </div>

      {/* ══════════════════════════ ÜRET ══════════════════════════ */}
      {sekme === 'uret' && (
        <div className="mt-5 grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)_300px]">
          {/* Sol: brief */}
          <div className="space-y-4">
            <Kutu
              baslik="Brief"
              sag={
                <button onClick={() => setBriefAcik((v) => !v)} className="text-slate-400 hover:text-slate-700">
                  {briefAcik ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              }
            >
              {briefAcik ? (
                <div className="space-y-3">
                  <div>
                    <Etiket>Başlık fikri</Etiket>
                    <Alan
                      deger={brief.baslik}
                      satir={2}
                      ipucu="Kiralık Araçta Kilometre Sınırı: Günlük Kaç KM Hakkınız Var?"
                      onIsle={(v) => setBrief((b) => ({ ...b, baslik: v }))}
                    />
                  </div>
                  <div>
                    <Etiket>Odak anahtar kelime</Etiket>
                    <TekSatir
                      deger={brief.odakKelime}
                      ipucu="araç kiralama km sınırı"
                      onIsle={(v) => setBrief((b) => ({ ...b, odakKelime: v }))}
                    />
                  </div>
                  <div>
                    <Etiket not="her satır bir kelime">Yardımcı kelimeler</Etiket>
                    <Alan
                      deger={(brief.yardimciKelimeler || []).join('\n')}
                      satir={4}
                      onIsle={(v) => setBrief((b) => ({ ...b, yardimciKelimeler: satirlaraBol(v) }))}
                    />
                  </div>
                  <div>
                    <Etiket not="her satır bir soru">Cevaplanacak sorular</Etiket>
                    <Alan
                      deger={(brief.sorular || []).join('\n')}
                      satir={4}
                      onIsle={(v) => setBrief((b) => ({ ...b, sorular: satirlaraBol(v) }))}
                    />
                  </div>
                  <div>
                    <Etiket>Arama niyeti</Etiket>
                    <TekSatir
                      deger={brief.aramaNiyeti}
                      ipucu="bilgi arıyor / karşılaştırıyor / işlem yapacak"
                      onIsle={(v) => setBrief((b) => ({ ...b, aramaNiyeti: v }))}
                    />
                  </div>
                  <div>
                    <Etiket>Ek not</Etiket>
                    <Alan deger={brief.notlar} satir={2} onIsle={(v) => setBrief((b) => ({ ...b, notlar: v }))} />
                  </div>
                  {/* Ticari koşul uydurmasının önündeki tek gerçek engel. Model
                      burada verilen rakamları kullanır, burada olmayan koşulu yazmaz. */}
                  <div>
                    <Etiket not="markanın kendi yayımladığı rakamlar, şartlar, ücretler — model yalnızca bunları kullanır">
                      Doğrulanmış olgular
                    </Etiket>
                    <Alan
                      deger={brief.olgular}
                      satir={6}
                      ipucu={'Ör: Araçlar günlük 400 km, 10 gün ve üzeri en çok 4.000 km ile sınırlıdır.\nEkonomik grup: en az 1 yıl ehliyet, 21 yaş.\nKaynak: Kiralama Koşulları, 11 Eylül 2026.'}
                      onIsle={(v) => setBrief((b) => ({ ...b, olgular: v }))}
                    />
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
                      Boş bırakırsanız model ticari koşulları kendi bilgisinden yazar. Rakamları
                      buraya yapıştırın; somut bilgi oranı da buradan yükselir.
                    </p>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={uret}
                      disabled={uretiliyor || !boot?.geminiHazir}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
                    >
                      {uretiliyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      {uretiliyor ? 'Yazılıyor…' : 'Gemini ile yaz'}
                    </button>
                    <button
                      onClick={yamyamlikBak}
                      title="Bu konu blogda zaten var mı?"
                      className="rounded-lg border border-slate-200 px-3 py-2.5 text-slate-600 hover:bg-slate-50"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </div>
                  {uretiliyor && (
                    <p className="text-[11px] leading-relaxed text-slate-400">
                      Uzun yazılarda 60–120 saniye sürebiliyor. Sekmeyi kapatma.
                    </p>
                  )}
                </div>
              ) : (
                <button onClick={() => setBriefAcik(true)} className="w-full text-left">
                  <p className="line-clamp-2 text-[13px] font-medium text-slate-800">{brief.baslik || '(başlıksız)'}</p>
                  <p className="mt-1 text-[12px] text-slate-500">{brief.odakKelime || '—'}</p>
                </button>
              )}
            </Kutu>

            {/* Yamyamlık uyarısı */}
            {benzer && (
              <Kutu baslik="Blogda benzeri var mı?">
                {benzer.eslesme?.length ? (
                  <div className="space-y-2">
                    <p className="text-[12px] leading-relaxed text-amber-700">
                      {benzer.eslesme.length} yazı bu konuya yakın. Aynı şeyi anlatan ikinci yazı ikisini birden düşürür —
                      ya açıyı değiştir ya mevcut yazıyı güncelle.
                    </p>
                    {benzer.eslesme.map((m) => (
                      <a
                        key={m.url}
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block rounded-lg border border-amber-200 bg-amber-50 p-2.5 hover:bg-amber-100"
                      >
                        <p className="text-[12px] font-medium leading-snug text-slate-800">{m.title}</p>
                        <p className="mt-0.5 text-[11px] text-amber-700">%{Math.round(m.skor * 100)} örtüşme</p>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="inline-flex items-center gap-1.5 text-[12px] text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Blogda benzer yazı yok · {nf(benzer.envanterAdet)} yazı tarandı
                  </p>
                )}
              </Kutu>
            )}

            {/* Hazır brief havuzu */}
            {tohum?.briefler?.length ? (() => {
              // Havuz varsayılan olarak SADECE yazılmamış konuları gösteriyor:
              // buradan seçilen brief'in daha önce yazılmamış olması gerekiyor.
              // İşaretleme sunucudan gelmediyse (istek düştü) hepsi gösteriliyor —
              // eksik bilgiyle konu saklamak, fazla göstermekten kötü.
              const durumu = (b) => briefDurum?.[b.no]?.durum || 'bos';
              const bos = tohum.briefler.filter((b) => durumu(b) === 'bos');
              const kullanilan = tohum.briefler.filter((b) => durumu(b) !== 'bos');

              return (
              <Kutu
                baslik={
                  briefDurum
                    ? `Hazır brief havuzu (${bos.length} boş / ${tohum.briefler.length})`
                    : `Hazır brief havuzu (${tohum.briefler.length})`
                }
                sag={
                  tohum.kaynakSayfa ? (
                    <a href={tohum.kaynakSayfa.yol} className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:underline">
                      {tohum.kaynakSayfa.ad} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null
                }
              >
                <p className="mb-2.5 text-[11px] leading-relaxed text-slate-500">
                  Kelime araştırmasından çıkan, blogda karşılığı olmayan konular. Tıkla, brief dolsun.
                </p>

                {bos.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-[12px] leading-relaxed text-slate-500">
                    Havuzdaki konuların hepsi yazılmış. Yeni konu için brief'i elle doldur ya da{' '}
                    {tohum.kaynakSayfa ? (
                      <a href={tohum.kaynakSayfa.yol} className="font-medium text-emerald-700 hover:underline">
                        {tohum.kaynakSayfa.ad}
                      </a>
                    ) : (
                      'kelime araştırması'
                    )}{' '}
                    sayfasından havuzu tazele.
                  </p>
                ) : null}

                <div className="space-y-1.5">
                  {bos.map((b) => (
                    <div key={b.no} className="rounded-lg border border-slate-200">
                      <button
                        onClick={() => setAciKume(aciKume === b.no ? null : b.no)}
                        className="flex w-full items-start gap-2 p-2.5 text-left hover:bg-slate-50"
                      >
                        <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded bg-slate-100 text-[10px] font-bold text-slate-500">
                          {b.no}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[12px] font-medium leading-snug text-slate-800">{b.baslik}</span>
                          <span className="mt-0.5 block text-[11px] text-slate-500">
                            {b.kume} · {b.hacim == null ? 'hacim ölçülemedi' : `${nf(b.hacim)} arama/ay`}
                          </span>
                        </span>
                      </button>
                      {aciKume === b.no && (
                        <div className="border-t border-slate-100 bg-slate-50 p-2.5">
                          {b.bosluk ? <p className="mb-2 text-[11px] leading-relaxed text-slate-600">{b.bosluk}</p> : null}
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Kelimeler</p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">
                            {[b.odakKelime, ...(b.yardimciKelimeler || [])].filter(Boolean).join(' · ')}
                          </p>
                          <button
                            onClick={() => briefDoldur(b)}
                            className="mt-2.5 w-full rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-slate-700"
                          >
                            Brief'i doldur
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {kullanilan.length ? (
                  <div className="mt-3 border-t border-slate-100 pt-2.5">
                    <button
                      onClick={() => setKullanilanGoster((v) => !v)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                    >
                      {kullanilanGoster ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      Yazılmış {kullanilan.length} konu
                    </button>
                    {kullanilanGoster ? (
                      <div className="mt-2 space-y-1.5">
                        {kullanilan.map((b) => {
                          const d = briefDurum[b.no];
                          return (
                            <div key={b.no} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5">
                              <p className="text-[12px] font-medium leading-snug text-slate-500 line-through decoration-slate-300">
                                {b.baslik}
                              </p>
                              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                                {d.durum === 'taslakta' ? (
                                  <>
                                    Arşivde:{' '}
                                    <button
                                      onClick={() => taslakAc(d.taslak.id)}
                                      className="font-medium text-emerald-700 hover:underline"
                                    >
                                      #{d.taslak.id} · {d.taslak.baslik}
                                    </button>{' '}
                                    <span className="text-slate-400">({d.taslak.durum})</span>
                                  </>
                                ) : (
                                  <>
                                    Yayında:{' '}
                                    <a
                                      href={d.yazi.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="font-medium text-emerald-700 hover:underline"
                                    >
                                      {d.yazi.baslik}
                                    </a>
                                  </>
                                )}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </Kutu>
              );
            })() : null}
          </div>

          {/* Orta: içerik */}
          <div className="space-y-4">
            {!icerik ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                <FileText className="h-10 w-10 text-slate-300" />
                <p className="mt-4 text-[15px] font-semibold text-slate-700">Henüz içerik yok</p>
                <p className="mt-1.5 max-w-[380px] text-[13px] leading-relaxed text-slate-500">
                  Soldan brief'i doldur ya da hazır havuzdan bir konu seç, sonra “Gemini ile yaz” de.
                  Çıkan metni burada satır satır düzenleyebilirsin.
                </p>
              </div>
            ) : (
              <>
                {/* Eylem çubuğu */}
                <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-2.5 backdrop-blur">
                  <button
                    onClick={() => kaydet('taslak')}
                    disabled={kaydediliyor}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-[13px] font-semibold text-white hover:bg-slate-700 disabled:opacity-40"
                  >
                    {kaydediliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    {taslakId ? 'Güncelle' : 'Arşive kaydet'}
                  </button>
                  {taslakId ? (
                    <button
                      onClick={() => kaydet('onayli')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-700 hover:bg-emerald-100"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Onayla
                    </button>
                  ) : null}
                  <span className="mx-1 h-5 w-px bg-slate-200" />
                  {[
                    ['md', 'Markdown'], ['html', 'HTML'], ['json', 'JSON'],
                  ].map(([k, ad]) => (
                    <button
                      key={k}
                      onClick={() => disaAktar(k)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Download className="h-3.5 w-3.5" /> {ad}
                    </button>
                  ))}
                  <span className="mx-1 h-5 w-px bg-slate-200" />
                  {[
                    ['kopyala-duz', 'Düz'], ['kopyala-md', 'MD'], ['kopyala-html', 'HTML'], ['kopyala-jsonld', 'JSON-LD'],
                  ].map(([k, ad]) => (
                    <button
                      key={k}
                      onClick={() => disaAktar(k)}
                      title={`${ad} panoya kopyala`}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
                    >
                      <Copy className="h-3.5 w-3.5" /> {ad}
                    </button>
                  ))}
                  {model ? <span className="ml-auto text-[11px] text-slate-400">{model}</span> : null}
                </div>

                {/* Meta */}
                <Kutu baslik="Başlık ve meta">
                  <div className="space-y-3">
                    <div>
                      <Etiket not="H1">Başlık</Etiket>
                      <TekSatir
                        deger={icerik.baslik}
                        limit={sinirlar?.baslikMax}
                        onIsle={(v) => gncIcerik({ baslik: v })}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Etiket>SEO başlığı</Etiket>
                        <TekSatir
                          deger={icerik.metaBaslik}
                          limit={sinirlar?.baslikMax}
                          onIsle={(v) => gncIcerik({ metaBaslik: v })}
                        />
                      </div>
                      <div>
                        <Etiket>Slug</Etiket>
                        <TekSatir deger={icerik.slug} onIsle={(v) => gncIcerik({ slug: v })} />
                      </div>
                    </div>
                    <div>
                      <Etiket not={`${sinirlar?.metaMin}–${sinirlar?.metaMax} karakter`}>Meta açıklama</Etiket>
                      <TekSatir
                        deger={icerik.metaAciklama}
                        limit={sinirlar?.metaMax}
                        onIsle={(v) => gncIcerik({ metaAciklama: v })}
                      />
                    </div>
                  </div>
                </Kutu>

                {/* Kapak görseli */}
                <Kutu
                  baslik="Kapak görseli"
                  sag={
                    icerik.gorsel ? (
                      <a
                        href={icerik.gorsel.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                      >
                        Tam boy <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null
                  }
                >
                  {icerik.gorsel ? (
                    <img
                      src={icerik.gorsel.url}
                      alt={icerik.gorsel.alt || icerik.baslik}
                      className="mb-3 w-full rounded-lg border border-slate-200 object-cover"
                    />
                  ) : (
                    <div className="mb-3 flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-[12px] text-slate-400">
                      Henüz görsel yok
                    </div>
                  )}

                  {icerik.gorsel?.ozet ? (
                    <p className="mb-3 text-[12px] leading-relaxed text-slate-500">{icerik.gorsel.ozet}</p>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={gorselUret}
                      disabled={gorselUretiliyor}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      {gorselUretiliyor ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Çiziliyor…</>
                      ) : (
                        <><ImageIcon className="h-3.5 w-3.5" /> {icerik.gorsel ? 'Yeniden üret' : 'Görsel üret'}</>
                      )}
                    </button>

                    <select
                      value={gorselEnBoy}
                      onChange={(e) => setGorselEnBoy(e.target.value)}
                      className="rounded-lg border border-slate-300 px-2 py-1.5 text-[12px] text-slate-700"
                    >
                      {['16:9', '3:2', '4:3', '1:1', '4:5'].map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>

                    <button
                      onClick={() => setIstemAcik((v) => !v)}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-500 hover:text-slate-800"
                    >
                      {istemAcik ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      İstem
                    </button>

                    {icerik.gorsel ? (
                      <>
                        <button
                          onClick={() => { panoyaKopyala(icerik.gorsel.url); toast.success('Görsel adresi kopyalandı'); }}
                          className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-500 hover:text-slate-800"
                        >
                          <Copy className="h-3.5 w-3.5" /> Adres
                        </button>
                        <span className="text-[11px] text-slate-400">
                          {icerik.gorsel.enBoy} · {Math.round((icerik.gorsel.bayt || 0) / 1024)} KB
                        </span>
                      </>
                    ) : null}
                  </div>

                  {istemAcik ? (
                    <div className="mt-3">
                      <Etiket not="boş bırakırsan modele yazıyı okutup istemi o yazar">
                        Görsel istemi (İngilizce)
                      </Etiket>
                      <Alan
                        deger={gorselIstem}
                        satir={5}
                        ipucu="Boş bırak — yazıdan otomatik çıkarılsın"
                        onIsle={setGorselIstem}
                      />
                    </div>
                  ) : null}
                </Kutu>

                {/* Giriş */}
                <Kutu baslik="Giriş">
                  <Alan
                    deger={(icerik.girisParagraflari || []).join('\n\n')}
                    satir={5}
                    ipucu="Paragrafları boş satırla ayır"
                    onIsle={(v) => gncIcerik({ girisParagraflari: paragraflaraBol(v) })}
                  />
                </Kutu>

                {/* Bölümler */}
                <Kutu
                  baslik={`Bölümler (${(icerik.bolumler || []).length})`}
                  sag={
                    <button onClick={bolumEkle} className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-700 hover:underline">
                      <Plus className="h-3.5 w-3.5" /> Bölüm ekle
                    </button>
                  }
                >
                  <div className="space-y-4">
                    {(icerik.bolumler || []).map((b, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="flex-none rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">H2</span>
                          <div className="min-w-0 flex-1">
                            <TekSatir deger={b.h2} onIsle={(v) => gncBolum(i, { h2: v })} />
                          </div>
                          <button onClick={() => bolumSil(i)} className="flex-none text-slate-300 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <Alan
                          deger={(b.paragraflar || []).join('\n\n')}
                          satir={5}
                          ipucu="Paragraflar (boş satırla ayır)"
                          onIsle={(v) => gncBolum(i, { paragraflar: paragraflaraBol(v) })}
                        />
                        {(b.maddeler?.length || 0) > 0 && (
                          <div className="mt-2">
                            <Etiket not="her satır bir madde">Liste</Etiket>
                            <Alan
                              deger={(b.maddeler || []).join('\n')}
                              satir={3}
                              onIsle={(v) => gncBolum(i, { maddeler: satirlaraBol(v) })}
                            />
                          </div>
                        )}
                        {(b.altBolumler || []).map((a, j) => (
                          <div key={j} className="mt-3 border-l-2 border-slate-100 pl-3">
                            <div className="mb-1.5 flex items-center gap-2">
                              <span className="flex-none rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-400">H3</span>
                              <div className="min-w-0 flex-1">
                                <TekSatir
                                  deger={a.h3}
                                  onIsle={(v) => {
                                    const alt = [...b.altBolumler];
                                    alt[j] = { ...alt[j], h3: v };
                                    gncBolum(i, { altBolumler: alt });
                                  }}
                                />
                              </div>
                            </div>
                            <Alan
                              deger={(a.paragraflar || []).join('\n\n')}
                              satir={3}
                              onIsle={(v) => {
                                const alt = [...b.altBolumler];
                                alt[j] = { ...alt[j], paragraflar: paragraflaraBol(v) };
                                gncBolum(i, { altBolumler: alt });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </Kutu>

                {/* Sonuç + CTA */}
                <Kutu baslik="Sonuç ve çağrı">
                  <div className="space-y-3">
                    <div>
                      <Etiket>Sonuç</Etiket>
                      <Alan deger={icerik.sonuc} satir={3} onIsle={(v) => gncIcerik({ sonuc: v })} />
                    </div>
                    <div>
                      <Etiket>CTA</Etiket>
                      <Alan deger={icerik.cta} satir={2} onIsle={(v) => gncIcerik({ cta: v })} />
                    </div>
                  </div>
                </Kutu>

                {/* SSS */}
                {(icerik.sss || []).length > 0 && (
                  <Kutu baslik={`SSS (${icerik.sss.length})`}>
                    <div className="space-y-3">
                      {icerik.sss.map((q, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 p-3">
                          <TekSatir deger={q.soru} onIsle={(v) => gncSss(i, { soru: v })} />
                          <div className="mt-2">
                            <Alan deger={q.cevap} satir={3} onIsle={(v) => gncSss(i, { cevap: v })} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Kutu>
                )}

                {/* Yayın notları */}
                <Kutu baslik="Yayın notları">
                  <div className="space-y-4 text-[13px]">
                    {icerik.icLinkler?.length ? (
                      <div>
                        <Etiket not="havuzdaki gerçek URL'ler">İç linkler</Etiket>
                        <ul className="space-y-1.5">
                          {icerik.icLinkler.map((l, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-emerald-500" />
                              <span className="min-w-0">
                                <a href={l.url} target="_blank" rel="noreferrer" className="font-medium text-emerald-700 hover:underline">
                                  {l.ankor || l.url}
                                </a>
                                {l.neden ? <span className="text-slate-500"> — {l.neden}</span> : null}
                                <span className="block break-all text-[11px] text-slate-400">{l.url}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {icerik.gorselOnerileri?.length ? (
                      <div>
                        <Etiket>Görsel önerileri</Etiket>
                        <ul className="space-y-1.5">
                          {icerik.gorselOnerileri.map((g, i) => (
                            <li key={i} className="rounded-lg bg-slate-50 p-2.5">
                              <p className="text-[12px] font-semibold text-slate-700">{g.yer}</p>
                              <p className="mt-0.5 text-[12px] leading-relaxed text-slate-600">{g.aciklama}</p>
                              <p className="mt-0.5 text-[11px] text-slate-400">alt: {g.alt}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {icerik.notlar?.length ? (
                      <div>
                        <Etiket>Yayın öncesi doğrulanacaklar</Etiket>
                        <ul className="space-y-1">
                          {icerik.notlar.map((n, i) => (
                            <li key={i} className="flex items-start gap-2 text-[12px] leading-relaxed text-amber-700">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" /> {n}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </Kutu>

                {/* SEO gerekçesi — yazıyla birlikte kaydedilir, müşteriye giden
                    dosyanın "neden bu yazı" bölümü buradan çıkar. */}
                <Kutu
                  baslik="SEO gerekçesi"
                  sag={
                    <button
                      onClick={gerekceDoldur}
                      disabled={gerekceDolduruluyor}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      {gerekceDolduruluyor
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Target className="h-3.5 w-3.5" />}
                      Veriden doldur
                    </button>
                  }
                >
                  <p className="mb-3 text-[12px] leading-relaxed text-slate-500">
                    Yazı başına sorulan altı soru. Boş alanlar veriden doldurulur, yazdıklarınız korunur.
                    Bu alanlar taslakla birlikte kaydedilir ve dışa aktarımda yer alır.
                  </p>
                  {gerekceOlcum?.dar ? (
                    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">
                      <span className="font-semibold text-slate-700">Ölçüm</span>{' '}
                      son {gerekceOlcum.gun} gün · {nf(gerekceOlcum.dar.gosterim)} gösterim ·{' '}
                      {nf(gerekceOlcum.dar.tik)} tık · sıra {gerekceOlcum.dar.sira ?? '—'} ·{' '}
                      {nf(gerekceOlcum.dar.sorgu)} sorgu
                    </div>
                  ) : null}
                  <div className="space-y-3">
                    {GEREKCE_ALANLARI.map(({ anahtar, etiket, ipucu }) => {
                      const dolu = String(seoGerekce?.[anahtar] || '').trim().length > 0;
                      return (
                        <div key={anahtar}>
                          <Etiket not={ipucu}>
                            <span className="inline-flex items-center gap-1.5">
                              {dolu
                                ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                                : <AlertTriangle className="h-3 w-3 text-amber-500" />}
                              {etiket}
                            </span>
                          </Etiket>
                          <Alan
                            deger={seoGerekce?.[anahtar] || ''}
                            satir={2}
                            onIsle={(v) => setSeoGerekce((g) => ({ ...g, [anahtar]: v, kaynak: 'duzenlendi' }))}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Kutu>
              </>
            )}
          </div>

          {/* Sağ: denetim */}
          <div className="space-y-4">
            <div className="sticky top-4 space-y-4">
              <Kutu baslik="Sınır denetimi">
                {!denetim ? (
                  <p className="text-[12px] leading-relaxed text-slate-400">
                    İçerik üretildiğinde ya da düzenlendiğinde sınırlar burada ölçülür.
                  </p>
                ) : (
                  <>
                    <div className="mb-3 flex items-end gap-3">
                      <div>
                        <p className={`text-[34px] font-bold leading-none tabular-nums ${puanRenk}`}>{puan}</p>
                        <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">uyum puanı</p>
                      </div>
                      <div className="mb-0.5 flex gap-2 text-[11px]">
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">{denetim.ozet.gecti} geçti</span>
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700">{denetim.ozet.uyari} uyarı</span>
                        <span className="rounded bg-rose-50 px-1.5 py-0.5 font-semibold text-rose-700">{denetim.ozet.kaldi} kaldı</span>
                      </div>
                    </div>
                    <p className="mb-3 text-[11px] text-slate-400">
                      {nf(denetim.ozet.kelime)} kelime · hedef {nf(sinirlar?.kelimeMin)}–{nf(sinirlar?.kelimeMax)}
                    </p>
                    <ul className="space-y-1.5">
                      {denetim.kurallar.map((k, i) => {
                        const ikon = k.durum === 'gecti'
                          ? <CheckCircle2 className="h-3.5 w-3.5 flex-none text-emerald-500" />
                          : k.durum === 'uyari'
                            ? <AlertTriangle className="h-3.5 w-3.5 flex-none text-amber-500" />
                            : <XCircle className="h-3.5 w-3.5 flex-none text-rose-500" />;
                        return (
                          <li key={i} className="flex items-start gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50">
                            <span className="mt-0.5">{ikon}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-[12px] font-medium leading-snug text-slate-700">{k.ad}</span>
                              <span className="block text-[11px] text-slate-500">
                                {k.olcum} <span className="text-slate-300">/</span> {k.hedef}
                              </span>
                              {k.not ? <span className="mt-0.5 block text-[11px] leading-snug text-slate-400">{k.not}</span> : null}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </Kutu>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════ SINIRLAR ═════════════════════════ */}
      {/* ═══════════════════════ LOKASYON ═══════════════════════ */}
      {/* Ofis sayfalarinin bos govdesi. Konu serbest degil, SAYFA belli:
          79 ofis sayfasi zaten yayinda, eksik olan tek sey govde metni. */}
      {sekme === 'lokasyon' && <LokasyonStudyosu brandId={brandId} headers={headers} />}

      {sekme === 'sinirlar' && sinirlar && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <Kutu baslik="Marka kimliği">
              <div className="space-y-3">
                <div>
                  <Etiket>Dil</Etiket>
                  <div className="flex gap-1.5">
                    {[['tr', 'Türkçe'], ['en', 'İngilizce']].map(([k, ad]) => (
                      <button
                        key={k}
                        onClick={() => { setSinirlar((s) => ({ ...s, dil: k })); setSinirKirli(true); }}
                        className={`rounded-lg px-3 py-1.5 text-[13px] font-medium ${
                          sinirlar.dil === k ? 'bg-emerald-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {ad}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Etiket not="model bunu okuyup üslubu kuruyor">Marka sesi</Etiket>
                  <Alan deger={sinirlar.markaSesi} satir={4} onIsle={(v) => { setSinirlar((s) => ({ ...s, markaSesi: v })); setSinirKirli(true); }} />
                </div>
                <div>
                  <Etiket>Okuyucu</Etiket>
                  <Alan deger={sinirlar.okuyucu} satir={3} onIsle={(v) => { setSinirlar((s) => ({ ...s, okuyucu: v })); setSinirKirli(true); }} />
                </div>
                <div>
                  <Etiket>Ton</Etiket>
                  <Alan deger={sinirlar.ton} satir={2} onIsle={(v) => { setSinirlar((s) => ({ ...s, ton: v })); setSinirKirli(true); }} />
                </div>
                <div>
                  <Etiket>Çağrı (CTA)</Etiket>
                  <Alan deger={sinirlar.cta} satir={2} onIsle={(v) => { setSinirlar((s) => ({ ...s, cta: v })); setSinirKirli(true); }} />
                </div>
              </div>
            </Kutu>

            <Kutu baslik="Ölçülebilir sınırlar">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[
                  ['kelimeMin', 'Kelime min'], ['kelimeMax', 'Kelime max'],
                  ['baslikMax', 'Başlık max krk'], ['metaMin', 'Meta min krk'],
                  ['metaMax', 'Meta max krk'], ['h2Min', 'H2 min'],
                  ['h2Max', 'H2 max'], ['paragrafMaxCumle', 'Paragraf max cümle'],
                  ['sssAdet', 'SSS sayısı'], ['icLinkMin', 'İç link min'],
                ].map(([k, ad]) => (
                  <div key={k}>
                    <Etiket>{ad}</Etiket>
                    <Sayi deger={sinirlar[k]} onIsle={(v) => { setSinirlar((s) => ({ ...s, [k]: v })); setSinirKirli(true); }} />
                  </div>
                ))}
                <div>
                  <Etiket>Yoğunluk min %</Etiket>
                  <Sayi adim={0.1} deger={sinirlar.yogunlukMin} onIsle={(v) => { setSinirlar((s) => ({ ...s, yogunlukMin: v })); setSinirKirli(true); }} />
                </div>
                <div>
                  <Etiket>Yoğunluk max %</Etiket>
                  <Sayi adim={0.1} deger={sinirlar.yogunlukMax} onIsle={(v) => { setSinirlar((s) => ({ ...s, yogunlukMax: v })); setSinirKirli(true); }} />
                </div>
              </div>
            </Kutu>
          </div>

          <div className="space-y-4">
            <Kutu baslik="Yasak ifadeler">
              <p className="mb-2 text-[11px] leading-relaxed text-slate-500">
                Her satır bir ifade. Denetimde <strong className="font-semibold text-slate-700">tek geçiş bile taslağı düşürür</strong> —
                rakip markaları ve iddia kalıplarını buraya yaz.
              </p>
              <Alan
                deger={(sinirlar.yasakIfadeler || []).join('\n')}
                satir={8}
                onIsle={(v) => { setSinirlar((s) => ({ ...s, yasakIfadeler: satirlaraBol(v) })); setSinirKirli(true); }}
              />
            </Kutu>
            <Kutu baslik="Zorunlu ifadeler">
              <p className="mb-2 text-[11px] text-slate-500">Metinde birebir geçmesi gereken ifadeler. Her satır bir tane.</p>
              <Alan
                deger={(sinirlar.zorunluIfadeler || []).join('\n')}
                satir={3}
                onIsle={(v) => { setSinirlar((s) => ({ ...s, zorunluIfadeler: satirlaraBol(v) })); setSinirKirli(true); }}
              />
            </Kutu>
            <Kutu baslik="Değinilmeyecek konular">
              <p className="mb-2 text-[11px] text-slate-500">Prompt'a giriyor, denetimde ölçülmüyor. Her satır bir kural.</p>
              <Alan
                deger={(sinirlar.kacinilacakKonular || []).join('\n')}
                satir={4}
                onIsle={(v) => { setSinirlar((s) => ({ ...s, kacinilacakKonular: satirlaraBol(v) })); setSinirKirli(true); }}
              />
            </Kutu>
            <Kutu baslik="Ek talimat">
              <Alan
                deger={sinirlar.ekTalimat}
                satir={5}
                ipucu="Yapı, örnek verme biçimi, kaçınılacak kalıplar…"
                onIsle={(v) => { setSinirlar((s) => ({ ...s, ekTalimat: v })); setSinirKirli(true); }}
              />
            </Kutu>

            <div className="flex items-center gap-3">
              <button
                onClick={sinirlariKaydet}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-slate-700"
              >
                <Save className="h-4 w-4" /> Marka sınırlarını kaydet
              </button>
              {sinirKirli ? (
                <span className="text-[12px] text-amber-600">
                  {boot?.profilKayitli ? 'Kaydedilmemiş değişiklik var' : 'Bu marka için henüz kaydedilmedi — tohum değerler gösteriliyor'}
                </span>
              ) : (
                <span className="text-[12px] text-slate-400">
                  {boot?.profilGuncellendi ? `Son kayıt: ${new Date(boot.profilGuncellendi).toLocaleString('tr-TR')}` : 'Kayıtlı'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════ ARŞİV ══════════════════════════ */}
      {sekme === 'arsiv' && (
        <div className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setSecili(new Set(taslaklar.map((t) => t.id)))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Tümünü seç
            </button>
            <button
              onClick={() => setSecili(new Set())}
              className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Temizle
            </button>
            <button
              onClick={topluAktar}
              disabled={!secili.size}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" /> Seçilenleri ZIP indir ({secili.size})
            </button>
            <span className="text-[12px] text-slate-400">ZIP içinde her yazının .md ve .html hâli + index.csv + tumu.json</span>
          </div>

          {!taslaklar.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
              <Archive className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-[14px] font-semibold text-slate-700">Arşiv boş</p>
              <p className="mt-1 text-[13px] text-slate-500">Üret sekmesinde bir yazı hazırlayıp “Arşive kaydet” de.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="w-9 p-2.5" />
                    {['Başlık', 'Odak kelime', 'Dil', 'Kelime', 'Uyum', 'Durum', 'Güncellendi', ''].map((h, i) => (
                      <th key={i} className="whitespace-nowrap p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {taslaklar.map((t) => {
                    const d = durumBul(t.durum);
                    return (
                      <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-2.5">
                          <input
                            type="checkbox"
                            checked={secili.has(t.id)}
                            onChange={(e) => {
                              const s = new Set(secili);
                              if (e.target.checked) s.add(t.id); else s.delete(t.id);
                              setSecili(s);
                            }}
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                          />
                        </td>
                        <td className="max-w-[420px] p-2.5">
                          <button onClick={() => taslakAc(t.id)} className="text-left text-[13px] font-medium leading-snug text-slate-800 hover:text-emerald-700">
                            {t.baslik}
                          </button>
                          <p className="mt-0.5 truncate text-[11px] text-slate-400">/{t.slug}</p>
                        </td>
                        <td className="p-2.5 text-[12px] text-slate-600">{t.odakKelime || '—'}</td>
                        <td className="p-2.5 text-[12px] uppercase text-slate-500">{t.dil}</td>
                        <td className="p-2.5 text-[12px] tabular-nums text-slate-600">{nf(t.kelimeSayisi)}</td>
                        <td className="p-2.5">
                          <span className={`text-[12px] font-bold tabular-nums ${
                            t.puan == null ? 'text-slate-300'
                              : t.puan >= 90 ? 'text-emerald-600' : t.puan >= 70 ? 'text-amber-600' : 'text-rose-600'
                          }`}>
                            {t.puan == null ? '—' : t.puan}
                          </span>
                        </td>
                        <td className="p-2.5">
                          <select
                            value={t.durum}
                            onChange={(e) => durumDegistir(t.id, e.target.value)}
                            className={`rounded-md px-2 py-1 text-[11px] font-semibold outline-none ${d.renk}`}
                          >
                            {DURUMLAR.map((x) => <option key={x.k} value={x.k}>{x.ad}</option>)}
                          </select>
                        </td>
                        <td className="whitespace-nowrap p-2.5 text-[11px] text-slate-400">
                          {new Date(t.updatedAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="p-2.5 text-right">
                          <button onClick={() => taslakSil(t.id)} className="text-slate-300 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
